import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";

import { AppContexts } from "../../providers";
import ActivityFeed from "./ActivityFeed";
import DeviceHealthTable from "./DeviceHealthTable";
import { formatDateInput, getDeviceName } from "./utils";

const toApiStart = (date) => date ? moment(date).startOf("day").toISOString() : undefined;
const toApiEnd = (date) => date ? moment(date).endOf("day").toISOString() : undefined;

const SeamMonitoringPanel = ({
  title = "Smart Lock Monitoring",
  rentalId = null,
  guestReservationId = null,
  defaultStartDate = null,
  defaultEndDate = null,
  showFilters = true,
  showDevices = true,
  showTitle = true,
  showActivityDeviceName = true,
  showActivityActor = true,
  compact = false,
}) => {
  const { getMonitoringActivity, getMonitoringDevices, monitoringError } = useContext(AppContexts.SeamContext);
  const { rentals, get: getRentals } = useContext(AppContexts.RentalsContext);

  const [activity, setActivity] = useState([]);
  const [devices, setDevices] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const didRequestRentals = useRef(false);
  const [filters, setFilters] = useState({
    rental_id: rentalId || "",
    device_id: "",
    start_date: formatDateInput(defaultStartDate, moment().subtract(14, "days")),
    end_date: formatDateInput(defaultEndDate, moment()),
  });

  useEffect(() => {
    if (!rentalId && showFilters && (!rentals || rentals.length === 0) && !didRequestRentals.current) {
      didRequestRentals.current = true;
      getRentals();
    }
  }, [rentalId, showFilters, rentals, getRentals]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      rental_id: rentalId || "",
      device_id: "",
      start_date: formatDateInput(defaultStartDate, moment().subtract(14, "days")),
      end_date: formatDateInput(defaultEndDate, moment()),
    }));
  }, [rentalId, guestReservationId, defaultStartDate, defaultEndDate]);

  const scopedRentalId = rentalId || filters.rental_id || null;

  const loadDevices = useCallback(async () => {
    if (!showDevices && guestReservationId) return;

    setDevicesLoading(true);
    const rows = await getMonitoringDevices({
      rental_id: scopedRentalId || undefined,
      limit: compact ? 50 : 250,
    });
    setDevices(rows);
    setDevicesLoading(false);
  }, [showDevices, guestReservationId, getMonitoringDevices, scopedRentalId, compact]);

  const loadActivity = useCallback(async () => {
    setActivityLoading(true);
    const rows = await getMonitoringActivity({
      rental_id: scopedRentalId || undefined,
      guest_reservation_id: guestReservationId || undefined,
      device_id: filters.device_id || undefined,
      start_date: toApiStart(filters.start_date),
      end_date: toApiEnd(filters.end_date),
      limit: compact ? 50 : 100,
    });
    setActivity(rows);
    setActivityLoading(false);
  }, [getMonitoringActivity, scopedRentalId, guestReservationId, filters.device_id, filters.start_date, filters.end_date, compact]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  const visibleDevices = useMemo(() => {
    if (!filters.device_id) return devices;
    return devices.filter((device) => device.device_id === filters.device_id);
  }, [devices, filters.device_id]);

  const healthSummary = useMemo(() => {
    const total = visibleDevices.length;
    const offline = visibleDevices.filter((device) => device.is_online === false).length;
    const lowBattery = visibleDevices.filter((device) => device.is_low_battery === true).length;
    const unknown = visibleDevices.filter((device) => device.is_online === null || device.is_online === undefined).length;
    return { total, offline, lowBattery, unknown };
  }, [visibleDevices]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "rental_id" ? { device_id: "" } : {}),
    }));
  };

  return (
    <div className={`seam-monitoring-panel${compact ? " seam-monitoring-panel-compact" : ""}`}>
      <div className="seam-monitoring-header">
        {showTitle && title ? <h3>{title}</h3> : <span />}
        <button
          type="button"
          className="uk-button hms-btn hms-white-btn seam-refresh-btn"
          onClick={() => {
            loadDevices();
            loadActivity();
          }}
          disabled={activityLoading || devicesLoading}
        >
          Refresh
        </button>
      </div>

      {showFilters && (
        <div className="seam-monitoring-filters uk-grid-small" uk-grid="">
          {!rentalId && (
            <div className="uk-width-1-4@m uk-width-1-2@s">
              <label className="uk-form-label">Property</label>
              <select
                className="uk-select uk-form-small"
                value={filters.rental_id}
                onChange={(event) => handleFilterChange("rental_id", event.target.value)}
              >
                <option value="">All properties</option>
                {(rentals || []).map((rental) => (
                  <option key={rental.rental_id} value={rental.rental_id}>
                    {rental.name || rental.pms_id || rental.rental_id}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="uk-width-1-4@m uk-width-1-2@s">
            <label className="uk-form-label">Device</label>
            <select
              className="uk-select uk-form-small"
              value={filters.device_id}
              onChange={(event) => handleFilterChange("device_id", event.target.value)}
            >
              <option value="">All devices</option>
              {devices.map((device) => (
                <option key={device.device_id} value={device.device_id}>
                  {getDeviceName(device)}
                </option>
              ))}
            </select>
          </div>
          <div className="uk-width-1-4@m uk-width-1-2@s">
            <label className="uk-form-label">Start Date</label>
            <input
              className="uk-input uk-form-small"
              type="date"
              value={filters.start_date}
              onChange={(event) => handleFilterChange("start_date", event.target.value)}
            />
          </div>
          <div className="uk-width-1-4@m uk-width-1-2@s">
            <label className="uk-form-label">End Date</label>
            <input
              className="uk-input uk-form-small"
              type="date"
              value={filters.end_date}
              onChange={(event) => handleFilterChange("end_date", event.target.value)}
            />
          </div>
        </div>
      )}

      {monitoringError && <div className="uk-alert uk-alert-danger">{monitoringError}</div>}

      {showDevices && (
        <>
          <div className="seam-health-summary">
            <div><strong>{healthSummary.total}</strong><span>Devices</span></div>
            <div><strong>{healthSummary.offline}</strong><span>Offline</span></div>
            <div><strong>{healthSummary.lowBattery}</strong><span>Low battery</span></div>
            <div><strong>{healthSummary.unknown}</strong><span>Unknown</span></div>
          </div>
          <DeviceHealthTable devices={visibleDevices} loading={devicesLoading} />
        </>
      )}

      <div className="seam-monitoring-section-title">Recent Activity</div>
      <ActivityFeed
        activity={activity}
        loading={activityLoading}
        compact={compact}
        showDeviceName={showActivityDeviceName}
        showActor={showActivityActor}
      />
    </div>
  );
};

export default SeamMonitoringPanel;
